import * as React from "react";
import { useCallback } from "react";
import styled from "@mui/material/styles/styled";
import { KahunColorMap } from "fe-common/ui/assets/colorMap";
import { usePatientCase } from "../usePatientCase";
import { QualifiedFeature } from "fe-common/graphql";
import { AgentTooltip } from "fe-common/ui/AgentTooltip";
import { useDiagnosticNavigation } from "../useDiagnosticNavigation";
import { useScreenSize } from "../util/useScreenSize";
import { IconButton } from "@mui/material";
import { getQFAndRefinementsLines } from "fe-common/logic";

//region [[ Styles ]]

const StarIconView = styled(IconButton)(
  ({ theme, selected }: { theme?: any; selected: boolean }) => ({
    color: selected ? KahunColorMap.AccentLight : KahunColorMap.Default,
    fontSize: "20px",
    marginRight: "5px",
  }),
);

//endregion [[ Styles ]]

//region [[ Props ]]

export interface StarIconProps {
  checked: boolean;
  disease: QualifiedFeature;
}

//endregion [[ Props ]]

//region [[ Functions ]]
//endregion [[ Functions ]]

export const StarIcon = ({ ...props }: StarIconProps) => {
  const { handleRemoveFromPlan, handleIncludeInPlan } = usePatientCase();
  const { setToastOpen, setToastText } = useDiagnosticNavigation();
  const { isMobile } = useScreenSize();
  const [findingTitle] = getQFAndRefinementsLines(props.disease);

  const ADDED_TO_PLAN_TOOLTIP_TEXT = "Excluded From Plan";
  const REMOVED_FROM_PLAN_TOOLTIP_TEXT = "Included In Plan";

  const ADDED_TO_PLAN_TOAST_TEXT = findingTitle + " was added to Plan";
  const REMOVED_FROM_PLAN_TOAST_TEXT = findingTitle + " was removed from Plan";

  const onClickStar = useCallback(
    (event) => {
      event.stopPropagation();
      if (props.checked) {
        handleRemoveFromPlan(props.disease);
      } else {
        handleIncludeInPlan(props.disease);
      }

      if (isMobile) {
        setToastText(
          !props.checked
            ? ADDED_TO_PLAN_TOAST_TEXT
            : REMOVED_FROM_PLAN_TOAST_TEXT,
        );
        setToastOpen(true);
      }
    },

    [
      handleRemoveFromPlan,
      handleIncludeInPlan,
      props.checked,
      props.disease,
      setToastText,
      isMobile,
      ADDED_TO_PLAN_TOAST_TEXT,
      REMOVED_FROM_PLAN_TOAST_TEXT,
      setToastOpen,
    ],
  );

  return (
    <StarIconView selected={props.checked} onClick={onClickStar} size={"small"}>
      <AgentTooltip
        enterDelay={800}
        title={
          !props.checked
            ? ADDED_TO_PLAN_TOOLTIP_TEXT
            : REMOVED_FROM_PLAN_TOOLTIP_TEXT
        }
      >
        {props.checked === true ? (
          <i className="fas fa-star"></i>
        ) : (
          <i className="fal fa-star"></i>
        )}
      </AgentTooltip>
    </StarIconView>
  );
};
